import type { CurrentPregnancy } from './types'
import type { UseQueryResult } from '@tanstack/react-query'

import { useMutation, type UseMutationResult, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import http from '../../../../../lib/http'
import { queryClient } from '../../../../../lib/query-client'

export function useCurrentPregnancy (patientId?: string, callback?: () => void): UseQueryResult<{draft: boolean, data: CurrentPregnancy} | undefined, unknown> {
  return useQuery<
  {draft: boolean, data: CurrentPregnancy} | undefined,
  unknown,
  {draft: true, data: CurrentPregnancy} | undefined,
  [ 'current-pregnancy', string | undefined ]
  >({
    queryKey: [ 'current-pregnancy', patientId ],
    async queryFn () {
      if (!patientId) {
        return
      }

      const response = await http.getRequest(`/patients/${patientId}/maternity/current-pregnancy`)

      callback?.()

      return {
        ...response,
        data: response.data
      }
    },
    refetchOnWindowFocus: false,
    retry: false
  })
}

export function useCurrentPregnancySubmitMutation (patientId?: string, callback?: () => void): UseMutationResult<unknown, unknown, CurrentPregnancy, unknown> {
  return useMutation<unknown, unknown, CurrentPregnancy, unknown>({
    async mutationFn (currentPregnancy) {
      if (!patientId) {
        return
      }

      const currentPregnancyData: Partial<CurrentPregnancy> = {
        id: currentPregnancy.id,
        lmp: currentPregnancy.lmp,
        delivered: currentPregnancy.delivered
      }

      return await http.postRequest(`/patients/${patientId}/maternity/current-pregnancy`, currentPregnancyData)
    },
    onSuccess () {
      void queryClient.invalidateQueries({ queryKey: [ 'current-pregnancy', patientId ?? '' ] })
      void queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })

      toast.success('Current Pregnancy Successfully Saved', { position: 'bottom-center' })
      callback?.()
    },
    onError () {
      toast.error('Something went wrong while saving Current Pregnancy')
    }
  })
}

export function useCurrentPregnancyDeleteDraft (patientId?: string, callback?: () => void): UseMutationResult<unknown, unknown, null, unknown> {
  return useMutation<unknown, unknown, null, unknown>({
    async mutationFn () {
      if (!patientId) {
        return
      }

      return await http.deleteRequest(`/patients/${patientId}/maternity/current-pregnancy/draft`)
    },
    async onSuccess () {
      await queryClient.invalidateQueries({ queryKey: [ 'current-pregnancy', patientId ?? '' ] })
      await queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })
      callback?.()
    }
  })
}

export function useCurrentPregnancyDraftMutation (patientId?: string, callback?: () => void): UseMutationResult<unknown, unknown, Partial<CurrentPregnancy>, unknown> {
  return useMutation<unknown, unknown, Partial<CurrentPregnancy>, unknown>({
    async mutationFn (currentPregnancy) {
      if (!patientId) {
        return
      }

      queryClient.setQueriesData<{draft: boolean, data: CurrentPregnancy}>({ queryKey: [ 'current-pregnancy', patientId ?? '' ] }, (prev) => {
        if (!prev) {
          return
        }
        return {
          ...prev,
          draft: true
        }
      })

      return await http.putRequest(`/patients/${patientId}/maternity/current-pregnancy/draft`, currentPregnancy)
    },
    async onSuccess () {
      await queryClient.invalidateQueries({ queryKey: [ 'drafts', patientId ] })
      callback?.()
    }
  })
}
